import type { CameraManager } from '../camera-manager.class';
import type { CameraControlsType } from '../../../../utils/lazy-camera-controls';
import { MIN_FLOOR_DISTANCE } from '../helpers';

/**
 * Configure control defaults
 */
export const configureControlsCfn = (
	self: CameraManager,
	CameraControlsClass: CameraControlsType
): void => {
	const controls = self.controls;

	// Set default target to origin
	controls.setTarget(0, 0, 0, false);

	// Set default mouse button actions
	controls.mouseButtons.left = CameraControlsClass.ACTION.ROTATE;
	controls.mouseButtons.right = CameraControlsClass.ACTION.TRUCK;
	controls.mouseButtons.wheel = CameraControlsClass.ACTION.DOLLY;

	// Smooth transitions for zoom/rotation
	controls.smoothTime = 0.5;

	// Rest threshold controls when the 'rest' event fires as the camera
	// decelerates after user interaction.  A higher value fires sooner
	// (while the camera is still visibly moving), a lower value waits
	// until it is nearly motionless.  The default (0.01) is fine for
	// most cases but we set it explicitly for clarity.
	controls.restThreshold = 0.01;
};

/**
 * Configure camera constraints based on debug mode
 */
export const configureConstraintsCfn = (
	self: CameraManager
): void => {
	const controls = self.controls;

	if (!self.debugMode) {
		// Prevent camera from going below floor
		controls.maxPolarAngle = Math.PI / 2;
	} else {
		// Remove constraints in debug mode
		controls.maxPolarAngle = Math.PI;
	}
};

/**
 * Setup camera constraints
 */
export const setupConstraintsCfn = (
	self: CameraManager
): () => void => {
	configureConstraintsCfn(self);

	// Create floor restriction handler
	const floorRestrictionHandler = (): void => {
		if (self.debugMode) return;

		const floorPresent = self.isFloorPresentFn();
		if (floorPresent) {
			const currentY = self.camera.position.y;
			if (currentY < MIN_FLOOR_DISTANCE) {
				self.camera.position.y = MIN_FLOOR_DISTANCE;
			}
		}
	};

	self.controls.addEventListener('update', floorRestrictionHandler);

	return floorRestrictionHandler;
};

/**
 * Check if a text input element currently has focus.
 * Used to prevent camera keyboard controls from interfering with text editing.
 */
const isTextInputFocused = (): boolean => {
	const el = document.activeElement;
	if (!el) return false;
	const tag = el.tagName.toLowerCase();
	if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
	if (el.getAttribute('contenteditable') === 'true') return true;
	if (el.closest?.('[role="textbox"]')) return true;
	return false;
};

/**
 * Setup keyboard handlers
 */
export const setupKeyboardHandlersCfn = (
	self: CameraManager,
	CameraControlsClass: CameraControlsType
): { keydownHandler: (e: KeyboardEvent) => void; keyupHandler: (e: KeyboardEvent) => void } => {
	const controls = self.controls;
	const keyState = self.keyState;

	const updateMouseConfig = (): void => {
		if (keyState.shiftRight || keyState.shiftLeft) {
			controls.mouseButtons.left = CameraControlsClass.ACTION.TRUCK;
		} else if (keyState.controlRight || keyState.controlLeft) {
			controls.mouseButtons.left = CameraControlsClass.ACTION.DOLLY;
		} else {
			controls.mouseButtons.left = CameraControlsClass.ACTION.ROTATE;
		}
	};

	const keydownHandler = (event: KeyboardEvent): void => {
		// Skip camera navigation when user is typing in a text input
		if (!isTextInputFocused()) {
			switch (event.keyCode) {
				case 37: // Left arrow
					controls.truck(-10, 0, true);
					break;
				case 38: // Up arrow
					controls.elevate(10, true);
					break;
				case 39: // Right arrow
					controls.truck(10, 0, true);
					break;
				case 40: // Down arrow
					controls.elevate(-10, true);
					break;
			}

			self.camera.updateProjectionMatrix();
		}

		// Modifier key tracking stays outside the guard —
		// it affects mouse button behavior which is canvas-scoped
		if (event.code === 'ShiftRight') keyState.shiftRight = true;
		if (event.code === 'ShiftLeft') keyState.shiftLeft = true;
		if (event.code === 'ControlRight') keyState.controlRight = true;
		if (event.code === 'ControlLeft') keyState.controlLeft = true;
		updateMouseConfig();
	};

	const keyupHandler = (event: KeyboardEvent): void => {
		if (event.code === 'ShiftRight') keyState.shiftRight = false;
		if (event.code === 'ShiftLeft') keyState.shiftLeft = false;
		if (event.code === 'ControlRight') keyState.controlRight = false;
		if (event.code === 'ControlLeft') keyState.controlLeft = false;
		updateMouseConfig();
	};

	window.addEventListener('keydown', keydownHandler);
	window.addEventListener('keyup', keyupHandler);

	return { keydownHandler, keyupHandler };
};
